import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import type {
    LeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import {
    createTopNLeadsSearchRequestDto,
    selectSearchResultsDtoByLeadsType,
} from "app/pages/sales-intelligence/modules/leads-search";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { OpportunityListParams } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { opportunityListQueryKeyFactory } from "../queryKeyFactory";

export type FetchTopNLeadsDomainsParams = {
    domainsCount: number;
    listParams: OpportunityListParams;
};

type HookValue<P> = (params: P) => Promise<readonly string[]>;

export const useFetchTopNLeadsDomains = <P extends FetchTopNLeadsDomainsParams>(leadsType: LeadsType): HookValue<P> => {
    const queryClient = useQueryClient();

    const dto = useSelector(selectSearchResultsDtoByLeadsType);

    return async ({ domainsCount, listParams }: P) => {
        const updatedDto = createTopNLeadsSearchRequestDto(dto[leadsType], domainsCount);

        const searchResults = await queryClient.fetchQuery({
            queryKey: opportunityListQueryKeyFactory.leadsTableData(listParams.id, leadsType, updatedDto),
            queryFn: ({ signal }) =>
                salesApiService.opportunityListPage.fetchLeadsSearchResults(
                    { listParams, dto: updatedDto, leadsType: leadsType },
                    {
                        cancellation: signal,
                    },
                ),
        });

        if (is.undefined(searchResults)) {
            return [];
        }

        return searchResults.rows.map(getLeadDomainFromRow);
    };
};
